import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Loader2, Mail, KeyRound, Snowflake, Sun, Trash2, Send, History, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const ROLE_LABELS = { super_duper_admin: "Super-Duper Admin", super_admin: "Super Admin", finance: "Finance", kiosk_manager: "Kiosk Manager" };
const METHOD_LABELS = { password: "Password", google: "Google", google_2fa: "Google · 2FA" };
const BLANK = { name: "", email: "", role: "kiosk_manager", building_ids: [] };

const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

function UserAvatar({ u }) {
  if (u.avatar_url) return <img src={u.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />;
  const init = (u.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white flex items-center justify-center text-xs font-bold shrink-0">{init}</div>;
}

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [historyUser, setHistoryUser] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async (u) => {
    setHistoryUser(u); setHistory([]); setHistoryLoading(true);
    try {
      const r = await api.get(`/users/${u.id}/login-history`);
      setHistory(r.data);
    } catch (e) {
      toast.error("Could not load login history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/buildings").then((r) => setBuildings(r.data));
  }, []);

  const openNew = () => { setForm(BLANK); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ ...BLANK, ...u }); setEditId(u.id); setOpen(true); };

  const toggleBuilding = (id) => {
    const has = form.building_ids?.includes(id);
    setForm({ ...form, building_ids: has ? form.building_ids.filter((b) => b !== id) : [...(form.building_ids || []), id] });
  };

  const save = async () => {
    if (!form.name.trim() || (!editId && !form.email.trim())) return toast.error("Name and email are required");
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/users/${editId}`, { name: form.name, role: form.role, building_ids: form.building_ids });
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("Invite sent — they'll get an email to set their password");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (id, fn, okMsg) => {
    setBusyId(id);
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const toggleFreeze = (u) => runAction(u.id, () => api.put(`/users/${u.id}`, { active: u.active === false }), u.active === false ? "Account unfrozen" : "Account frozen");
  const resetPassword = (u) => runAction(u.id, () => api.post(`/users/${u.id}/send-reset`), "Password reset link emailed");
  const resendInvite = (u) => runAction(u.id, () => api.post(`/users/${u.id}/send-invite`), "Invite link emailed");
  const removeUser = (u) => runAction(u.id, () => api.delete(`/users/${u.id}`), "User removed").then(() => setConfirmDelete(null));

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Users</h1>
          <p className="text-slate-500 mt-1">Role-based access control</p>
        </div>
        <Button data-testid="user-new-button" onClick={openNew} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1.5" /> New user</Button>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Buildings</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last login</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => {
              const pending = u.invited && u.active !== false;
              return (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium text-slate-900 dark:text-white">
                    <div className="flex items-center gap-3">
                      <UserAvatar u={u} />
                      <span>{u.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500">{u.email}</TableCell>
                  <TableCell><Badge variant={u.role === "super_admin" || u.role === "super_duper_admin" ? "default" : "secondary"} className={u.role === "super_duper_admin" ? "bg-fuchsia-600 hover:bg-fuchsia-600" : undefined}>{ROLE_LABELS[u.role]}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-500">{u.role === "kiosk_manager" ? (u.building_ids?.length || 0) + " assigned" : "All"}</TableCell>
                  <TableCell>
                    {u.active === false
                      ? <Badge variant="destructive">Frozen</Badge>
                      : pending
                        ? <Badge variant="outline" className="text-amber-600 border-amber-300">Invite pending</Badge>
                        : (
                          <div className="flex flex-col gap-0.5">
                            <Badge variant="outline" className="text-emerald-600 border-emerald-300 w-fit">Active</Badge>
                            {u.invite_accepted_at && (
                              <span data-testid={`user-invite-accepted-${u.email}`} className="text-[11px] text-slate-400 inline-flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3 text-emerald-500" /> Invite accepted {fmtDate(u.invite_accepted_at)}
                              </span>
                            )}
                          </div>
                        )}
                  </TableCell>
                  <TableCell data-testid={`user-last-login-${u.email}`} className="text-xs text-slate-500">{u.last_login_at ? fmtDateTime(u.last_login_at) : "Never"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button data-testid={`user-edit-${u.email}`} variant="outline" size="sm" onClick={() => openEdit(u)}>Edit</Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button data-testid={`user-actions-${u.email}`} variant="ghost" size="icon" className="h-8 w-8" disabled={busyId === u.id}>
                            {busyId === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          {pending && (
                            <DropdownMenuItem data-testid={`user-resend-invite-${u.email}`} onClick={() => resendInvite(u)}>
                              <Send className="h-4 w-4 mr-2" /> Resend invite
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem data-testid={`user-login-history-${u.email}`} onClick={() => openHistory(u)}>
                            <History className="h-4 w-4 mr-2" /> Login history
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`user-reset-password-${u.email}`} onClick={() => resetPassword(u)}>
                            <KeyRound className="h-4 w-4 mr-2" /> Send password reset
                          </DropdownMenuItem>
                          {u.id !== me?.id && (
                            <>
                              <DropdownMenuItem data-testid={`user-freeze-${u.email}`} onClick={() => toggleFreeze(u)}>
                                {u.active === false
                                  ? <><Sun className="h-4 w-4 mr-2" /> Unfreeze account</>
                                  : <><Snowflake className="h-4 w-4 mr-2" /> Freeze account</>}
                              </DropdownMenuItem>
                              {me?.role === "super_duper_admin" && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem data-testid={`user-remove-${u.email}`} onClick={() => setConfirmDelete(u)} className="text-rose-600 focus:text-rose-600">
                                    <Trash2 className="h-4 w-4 mr-2" /> Remove user
                                  </DropdownMenuItem>
                                </>
                              )}
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Email</Label><Input data-testid="user-form-email" value={form.email} disabled={!!editId} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  {me?.role === "super_duper_admin" && <SelectItem value="super_duper_admin">Super-Duper Admin</SelectItem>}
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="kiosk_manager">Kiosk Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "kiosk_manager" && (
              <div>
                <Label>Assigned buildings</Label>
                <div className="mt-1.5 space-y-1.5 max-h-40 overflow-y-auto">
                  {buildings.map((b) => (
                    <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" data-testid={`user-building-${b.id}`} checked={form.building_ids?.includes(b.id)} onChange={() => toggleBuilding(b.id)} />
                      {b.name}
                    </label>
                  ))}
                  {!buildings.length && <p className="text-xs text-slate-400">No buildings available.</p>}
                </div>
              </div>
            )}
            {!editId && (
              <div className="flex items-start gap-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/30 px-3 py-2.5 text-xs text-indigo-700 dark:text-indigo-300">
                <Mail className="h-4 w-4 mt-0.5 shrink-0" />
                <span>We'll email an invite link so they can set their own password. goodnus.com users can also sign in with Google.</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="user-form-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editId ? "Save" : <><Send className="h-4 w-4 mr-1.5" /> Send invite</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) { setConfirmDelete(null); setConfirmText(""); } }}>        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <b>{confirmDelete?.email}</b> and revokes their access. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-1">
            <Label className="text-slate-600 text-xs">Type <b className="font-mono-x text-rose-600">REMOVE</b> to confirm</Label>
            <Input
              data-testid="user-remove-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="REMOVE"
              autoComplete="off"
              className="mt-1.5"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="user-remove-confirm"
              disabled={confirmText.trim().toUpperCase() !== "REMOVE"}
              onClick={() => { removeUser(confirmDelete); setConfirmText(""); }}
              className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:pointer-events-none"
            >Remove user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!historyUser} onOpenChange={(o) => !o && setHistoryUser(null)}>
        <DialogContent data-testid="login-history-dialog">
          <DialogHeader><DialogTitle>Login history — {historyUser?.name}</DialogTitle></DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto">
            {historyLoading ? (
              <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>
            ) : history.length ? (
              <table className="w-full text-sm" data-testid="login-history-table">
                <thead>
                  <tr className="text-left text-slate-400 text-xs border-b border-slate-100 dark:border-slate-800">
                    <th className="py-2">When</th>
                    <th className="py-2">Method</th>
                    <th className="py-2">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((e) => (
                    <tr key={e.id} className="border-b border-slate-50 dark:border-slate-800/50">
                      <td className="py-2 text-slate-700 dark:text-slate-300">{fmtDateTime(e.at)}</td>
                      <td className="py-2 text-slate-500">{METHOD_LABELS[e.method] || e.method}</td>
                      <td className="py-2 text-xs font-mono-x text-slate-400">{e.ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="py-8 text-center text-sm text-slate-400" data-testid="login-history-empty">No logins recorded yet.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
